import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Save } from "lucide-react";

export default function AutoModCard({ channelUsername }) {
  const [cfg, setCfg] = useState({ enabled: false, block_new_accounts_hours: 0, block_links: false, rate_limit_msgs: 0, banned_words: [] });
  const [wordsText, setWordsText] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!channelUsername) return;
    api.get(`/channels/${channelUsername}/automod`).then(r => {
      setCfg(r.data);
      setWordsText((r.data.banned_words || []).join(", "));
    });
  }, [channelUsername]);

  const save = async () => {
    const words = wordsText.split(",").map(w => w.trim()).filter(Boolean).slice(0, 100);
    const payload = { ...cfg, banned_words: words };
    await api.patch(`/channels/${channelUsername}/automod`, payload);
    setCfg(payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
  };

  const Row = ({ label, desc, children }) => (
    <div className="flex items-start justify-between gap-4 p-3 border border-white/10 rounded-md">
      <div className="flex-1">
        <div className="text-sm font-bold">{label}</div>
        <p className="text-xs text-zinc-400 mt-1">{desc}</p>
      </div>
      <div>{children}</div>
    </div>
  );

  return (
    <div className="sc-card p-6 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-[color:var(--sc-volt)]" />
        <div className="sc-heading text-xl font-bold">Auto-Moderation</div>
        <span className="ml-auto text-[10px] sc-mono tracking-widest uppercase px-2 py-1 rounded-sm" style={{ background: "var(--sc-volt)", color: "#0A0A0C" }}>PREMIUM PERK</span>
      </div>
      <p className="text-xs text-zinc-400">Automated chat protection so you can focus on streaming. Rules apply to your channel only.</p>

      <Row label="Master switch" desc="Enable all auto-mod rules below">
        <button data-testid="automod-master" onClick={() => setCfg({...cfg, enabled: !cfg.enabled})} className={`px-4 py-1.5 rounded-full text-xs font-bold sc-mono ${cfg.enabled ? "bg-[color:var(--sc-volt)] text-black" : "border border-white/10 text-zinc-400"}`}>{cfg.enabled ? "ON" : "OFF"}</button>
      </Row>

      <Row label="Block new accounts" desc="Silence chat from accounts younger than this many hours (0 = off)">
        <input data-testid="automod-new-hours" type="number" min={0} max={720} value={cfg.block_new_accounts_hours} onChange={e => setCfg({...cfg, block_new_accounts_hours: Number(e.target.value)})} className="sc-input w-24 text-sm" />
      </Row>

      <Row label="Block links" desc="Filter messages containing URLs">
        <button data-testid="automod-links" onClick={() => setCfg({...cfg, block_links: !cfg.block_links})} className={`px-4 py-1.5 rounded-full text-xs font-bold sc-mono ${cfg.block_links ? "bg-[color:var(--sc-orange)] text-black" : "border border-white/10 text-zinc-400"}`}>{cfg.block_links ? "ON" : "OFF"}</button>
      </Row>

      <Row label="Rate limit" desc="Max messages per user per 10 seconds (0 = off)">
        <input data-testid="automod-rate" type="number" min={0} max={20} value={cfg.rate_limit_msgs} onChange={e => setCfg({...cfg, rate_limit_msgs: Number(e.target.value)})} className="sc-input w-24 text-sm" />
      </Row>

      <div>
        <label className="sc-label block mb-2">Banned words (comma-separated, up to 100)</label>
        <textarea data-testid="automod-words" value={wordsText} onChange={e => setWordsText(e.target.value)} className="sc-input min-h-[70px] text-sm" placeholder="spam, badword, ..." />
      </div>

      <div className="flex items-center gap-3">
        <button data-testid="automod-save" onClick={save} className="sc-btn flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Save Auto-Mod</button>
        {saved && <span className="text-xs text-[color:var(--sc-volt)]">Saved ✓</span>}
      </div>

      <div className="text-[10px] text-zinc-500 pt-2 border-t border-white/5">
        Auto-mod runs server-side on every incoming chat message. All rules — banned words, links, rate limit, new-account block — are enforced live. Streamer and platform admins are exempt.
      </div>
    </div>
  );
}
